import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from 'hooks/useAppStore';
import { PageSkeleton } from 'components/ui/Skeleton';
import { ROUTES } from 'constants/routes';
import { SoftHomeWelcomeModal } from 'features/auth/components/SoftHomeWelcomeModal';
import { TimezoneDetectedNotice } from 'features/settings/components/TimezoneDetectedNotice';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const isLoading = useAppSelector((s) => s.auth.isLoading);
  const user = useAppSelector((s) => s.auth.user);
  const location = useLocation();

  if (isLoading && !isAuthenticated) {
    return <PageSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  const hasAdminAccess = Boolean(
    user?.roles?.some((role) => ['admin', 'super_admin', 'coordinator'].includes(role))
  );
  const hasLearnerAccess = Boolean(
    user?.hasApprovedMembership || user?.hasSoftHomeAffiliation
  );
  const isAccessRequestRoute = location.pathname === ROUTES.ACCESS_REQUEST;

  if (!hasLearnerAccess && !hasAdminAccess && !isAccessRequestRoute) {
    return <Navigate to={ROUTES.ACCESS_REQUEST} replace />;
  }

  // Soft-home users should not sit on the access-request wall.
  if (hasLearnerAccess && isAccessRequestRoute) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <>
      <SoftHomeWelcomeModal />
      <TimezoneDetectedNotice />
      {children}
    </>
  );
}
